import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch } from 'react-redux';
import {
  X, AlertCircle, CheckCircle, Loader2,
  User, MapPin, Briefcase, Heart, Settings,
  ChevronRight, ChevronLeft, Upload, Plus, Trash2,
} from 'lucide-react';
import { fetchAllEmployees } from '../../store/slices/employeeSlice';
import { RELATIONSHIP_OPTS } from '../../constants/relationshipOptions';
import { NEW_EMPLOYEE_IS_APPROVED } from '../../utils/employeeApproval';
import axios from 'axios';

// ─── Services ─────────────────────────────────────────────────────────────────
import employeeService from '../../services/EmployeeService';
import designationService from '../../services/DesignationService';
import departmentService from '../../services/DepartmentService';
import locationService from '../../services/LocationService';
import gradeService from '../../services/GradeService';
import shiftService from '../../services/ShiftService';
import employeeCategoryService from '../../services/EmployeeCategoryService';
import bankService from '../../services/BankService';
import bankBranchService from '../../services/BankBranchService';
import { getLoggedInServiceNo } from '../../utils/masterDataUtils';
import { isMasterApproved } from '../../utils/masterApproval';
import { NEW_BANK_IS_APPROVED } from '../../utils/employeeBankApproval';
import IsoDateInput from '../common/Isodateinput';
import ExperienceInput from '../common/Experienceinput';
import {
  sanitiseStaffId, checkStaffId,
  sanitiseEpf, checkEpf,
  sanitiseNic, normaliseNic, checkNic,
  checkDateOfBirth, maxDobIso,
  checkDateOfJoining, todayIso,
  sanitisePhone, checkPhone,
  checkEmail,
  sanitisePostal, checkPostal,
  parseExperience, checkExperience,
  sanitiseOtRate, checkOtRate,
  sanitiseGpa, checkGpa,
  sanitiseAccountNumber, checkAccountNumber,
  sanitiseInitials,
} from '../../utils/employeeValidation';

/* Sent as p_hebAccountType. Savings is preselected. */
const ACCOUNT_TYPES = [
  { value: 'Savings', label: 'Savings' },
  { value: 'Current', label: 'Current' },
];
const DEFAULT_ACCOUNT_TYPE = 'Savings';


// ─── Static Options ───────────────────────────────────────────────────────────
const TITLES = ['Mr', 'Mrs', 'Ms', 'Miss', 'Dr', 'Rev'];
const GENDERS = [{ value: 'M', label: 'Male' }, { value: 'F', label: 'Female' }];
const MARITAL_STATUSES = [
  { value: 'S', label: 'Single' }, { value: 'M', label: 'Married' },
  { value: 'D', label: 'Divorced' }, { value: 'W', label: 'Widowed' },
];

const RELIGIONS = [
  { value: 'B', label: 'Buddhist' }, { value: 'C', label: 'Christian' },
  { value: 'H', label: 'Hindu' }, { value: 'I', label: 'Islam' }, { value: 'O', label: 'Other' },
];
const BLOOD_GROUPS = [
  { value: 1, label: 'A+' }, { value: 2, label: 'A-' }, { value: 3, label: 'B+' },
  { value: 4, label: 'B-' }, { value: 5, label: 'AB+' }, { value: 6, label: 'AB-' },
  { value: 7, label: 'O+' }, { value: 8, label: 'O-' },
];

// ─── Tabs ─────────────────────────────────────────────────────────────────────
const TABS = [
  { id: 0, icon: User, label: 'Personal' },
  { id: 1, icon: MapPin, label: 'Address' },
  { id: 2, icon: Briefcase, label: 'Employment' },
  { id: 3, icon: Heart, label: 'Emergency' },
  { id: 4, icon: Settings, label: 'Other' },
];

// ─── Empty Form ───────────────────────────────────────────────────────────────
const EMPTY = {
  serviceNo: '', epfNo: '', etfNo: '', nicNo: '', passportNo: '',
  title: 'Mr', gender: 'M',
  firstName: '', middleName: '', lastName: '', initials: '', reportName: '',
  dateOfBirth: '', maritalStatus: 'S', religion: '', bloodGroup: '',
  mobileNo: '', homeNo: '', officeNo: '', personalEmail: '', companyEmail: '',
  altContactName: '', altContactRelationship: '', altContactNumber: '', altContactAddress: '',
  permanentAddressLine1: '', permanentAddressLine2: '', permanentAddressLine3: '', permanentAddressCity: '', permanentAddressPostal: '',
  currentAddressLine1: '', currentAddressLine2: '', currentAddressLine3: '', currentAddressCity: '', currentAddressPostal: '',
  designationId: '', locationId: '', gradeId: '', employmentCategoryId: '',
  shiftId: '', dateOfJoining: '', noticePeriodDays: '', otRate: '', totalExperience: '',
  reportingEmployeeId: '', costCenter: '', group: '',
  bankId: '', branchId: '', accountNumber: '',
  remarks: '',
};


const EMPTY_FILES = { profilePic: null, birthCertificate: null, nicDocument: null, qualificationCertificate: null, traineeCertificate: null, otherCertificate: null };

// ─── Helpers ──────────────────────────────────────────────────────────────────
const fmt = (v) => (!v || v === 'null') ? '' : String(v).trim();

const extractList = (res) => {
  if (!res) return [];
  const data = res?.data ?? res;
  if (!data) return [];
  if (Array.isArray(data)) return data;
  if (Array.isArray(data.ResultSet)) return data.ResultSet;
  if (Array.isArray(data.resultSet)) return data.resultSet;
  if (Array.isArray(data.Result)) return data.Result;
  if (Array.isArray(data.result)) return data.result;
  if (Array.isArray(data.Data)) return data.Data;
  if (Array.isArray(data.data)) return data.data;
  return [];
};

const pick = (obj, hints, fallback = '') => {
  if (!obj || typeof obj !== 'object') return fallback;

  for (const h of hints) {
    if (obj[h] !== undefined && obj[h] !== null && obj[h] !== '') return obj[h];
  }

  const objKeys = Object.keys(obj).map(k => ({ raw: k, norm: String(k).replace(/[^a-zA-Z0-9]/g, '').toUpperCase() }));
  for (const h of hints) {
    const hNorm = String(h).replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
    if (!hNorm) continue;
    let match = objKeys.find(k => k.norm === hNorm);
    if (!match) match = objKeys.find(k => k.norm.endsWith(hNorm));
    if (!match && hNorm.length >= 3) match = objKeys.find(k => k.norm.includes(hNorm));
    if (match && obj[match.raw] !== undefined && obj[match.raw] !== null && obj[match.raw] !== '') {
      return obj[match.raw];
    }
  }
  return fallback;
};

const toOpts = (list, idKeys = [], nameKeys = []) => {
  if (!Array.isArray(list)) return [];
  return list.map(row => {
    if (!row || typeof row !== 'object') return null;
    const id = pick(row, [...idKeys, 'Id', 'id', 'Code', 'code', 'ID', 'KEY', 'key']);
    const name = pick(row, [...nameKeys, 'Name', 'name', 'Description', 'description', 'Desc', 'desc', 'Title', 'title', 'LABEL', 'label']);
    if (id === undefined || id === null || id === '') return null;
    return { value: String(id), label: String(name || id) };
  }).filter(Boolean);
};


const safeFetch = async (primaryFn, fallbackUrl) => {
  try {
    const res = await primaryFn();
    const list = extractList(res);
    if (list && list.length > 0) return list;
  } catch (e) { }
  if (fallbackUrl) {
    try {
      const res = await axios.get(fallbackUrl);
      const list = extractList(res);
      if (list && list.length > 0) return list;
    } catch (e) { }
  }
  return [];
};

const extraAddressTypeCode = (index) => String.fromCharCode('D'.charCodeAt(0) + index);

// ─── UI Atoms ─────────────────────────────────────────────────────────────────
function Toast({ message, type, onClose }) {
  useEffect(() => { const t = setTimeout(onClose, 4500); return () => clearTimeout(t); }, [onClose]);
  const cfg = {
    error: { bg: 'bg-red-600', icon: <AlertCircle size={16} /> },
    success: { bg: 'bg-emerald-600', icon: <CheckCircle size={16} /> },
    warning: { bg: 'bg-amber-500', icon: <AlertCircle size={16} /> },
  };
  const c = cfg[type] || cfg.error;
  return (
    <div className={`fixed top-4 right-4 z-[9999] flex items-center gap-3 px-4 py-3 rounded-lg shadow-2xl text-white text-sm font-medium max-w-xs ${c.bg}`}>
      {c.icon}<span className="flex-1">{message}</span>
      <button onClick={onClose} className="opacity-70 hover:opacity-100 ml-1"><X size={14} /></button>
    </div>
  );
}

function Field({ label, required, error, children }) {
  return (
    <div className="flex flex-col gap-1">
      <label className="text-[11px] font-medium text-gray-500">
        {label}{required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      {children}
      {error && <span className="text-[10px] text-red-500 flex items-center gap-0.5 mt-0.5"><AlertCircle size={9} />{error}</span>}
    </div>
  );
}

/* One field shape for the whole app: 36px tall, rounded-lg, gray-200 border,
   primary focus. Same as the master-file entry panels and payroll toolbars. */
const iCls = (err) =>
  `w-full h-9 border rounded-lg px-2.5 text-xs outline-none transition-colors
   ${err ? 'border-red-300 bg-red-50 focus:border-red-400' : 'border-gray-200 bg-white text-gray-800 focus:border-primary-500'}`;

function Inp({ value, onChange, placeholder, type = 'text', error, id }) {
  return <input id={id} type={type} value={value || ''} onChange={e => onChange(e.target.value)} placeholder={placeholder} className={iCls(error)} />;
}

// function Sel({ value, onChange, options = [], placeholder, error, loading, id }) {
//   return (
//     <div className="relative">
//       <select id={id} value={value || ''} onChange={e => onChange(e.target.value)} className={`${iCls(error)} appearance-none pr-6`} disabled={loading}>
//         <option value="">{loading ? 'Loading...' : (placeholder || '— Select —')}</option>
//         {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
//       </select>
//       <svg className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400" width="12" height="12" viewBox="0 0 12 12" fill="none">
//         <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
//       </svg>
//     </div>
//   );
// }

function Sel({ value, onChange, options = [], placeholder, error, loading, id, clearable = true }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [coords, setCoords] = useState({ top: 0, left: 0, width: 0 });
  const triggerRef = useRef(null);
  const panelRef = useRef(null);

  useEffect(() => {
    const onOutside = (e) => {
      if (
        triggerRef.current && !triggerRef.current.contains(e.target) &&
        panelRef.current && !panelRef.current.contains(e.target)
      ) {
        setOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', onOutside);
    return () => document.removeEventListener('mousedown', onOutside);
  }, []);

  const openDropdown = () => {
    if (loading) return;
    const rect = triggerRef.current.getBoundingClientRect();
    setCoords({ top: rect.bottom + window.scrollY + 4, left: rect.left + window.scrollX, width: rect.width });
    setOpen(o => !o);
  };

  const selected = options.find(o => String(o.value) === String(value));
  const filtered = query
    ? options.filter(o => o.label.toLowerCase().includes(query.toLowerCase()))
    : options;

  return (
    <div className="relative">
      <button
        type="button"
        id={id}
        ref={triggerRef}
        disabled={loading}
        onClick={openDropdown}
        className={`${iCls(error)} appearance-none pr-6 text-left flex items-center bg-white ${loading ? 'opacity-60 cursor-not-allowed' : ''}`}
      >
        <span className={`truncate ${selected ? 'text-gray-700' : 'text-gray-400'}`}>
          {loading ? 'Loading...' : (selected ? selected.label : (placeholder || '— Select —'))}
        </span>
      </button>
      <svg className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400" width="12" height="12" viewBox="0 0 12 12" fill="none">
        <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>

      {open && !loading && createPortal(
        <div
          ref={panelRef}
          style={{ position: 'fixed', top: coords.top, left: coords.left, width: coords.width, zIndex: 9999 }}
          className="bg-white border border-gray-200 rounded shadow-lg max-h-56 flex flex-col"
        >
          {/* A search box over a handful of options is just noise. */}
          {options.length > 5 && (
            <input
              autoFocus
              type="text"
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Type to search..."
              className="w-full border-b border-gray-200 px-2.5 py-1.5 text-[12px] focus:outline-none"
            />
          )}
          <div className="overflow-y-auto">
            {clearable && (
              <div
                onClick={() => { onChange(''); setOpen(false); setQuery(''); }}
                className="px-2.5 py-1.5 text-[12px] text-gray-400 hover:bg-gray-50 cursor-pointer"
              >
                {placeholder || '— Select —'}
              </div>
            )}
            {filtered.length === 0 && (
              <div className="px-2.5 py-1.5 text-[12px] text-gray-400">No matches</div>
            )}
            {filtered.map(o => (
              <div
                key={o.value}
                onClick={() => { onChange(o.value); setOpen(false); setQuery(''); }}
                className={`px-2.5 py-1.5 text-[12px] cursor-pointer hover:bg-primary-50 ${String(o.value) === String(value) ? 'bg-primary-50 text-primary-700 font-medium' : 'text-gray-700'}`}
              >
                {o.label}
              </div>
            ))}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}


function FileField({ label, file, onChange, accept = 'image/*,.pdf', error }) {
  const inputId = `file-${label.replace(/\s+/g, '-').toLowerCase()}`;
  return (
    <div className="flex flex-col gap-0.5">
      <label className="text-[11px] font-semibold text-gray-600">{label}</label>
      <label
        htmlFor={inputId}
        className={`flex items-center justify-between gap-2 border rounded px-2.5 py-1.5 text-[13px] bg-white cursor-pointer hover:bg-gray-50 transition-colors
          ${error ? 'border-red-300 bg-red-50' : 'border-gray-200'}`}
      >
        <span className={`truncate ${file ? 'text-gray-700' : 'text-gray-400'}`}>
          {file ? file.name : 'Choose file...'}
        </span>
        <Upload size={13} className="text-gray-400 shrink-0" />
      </label>
      <input
        id={inputId}
        type="file"
        accept={accept}
        className="hidden"
        onChange={e => onChange(e.target.files?.[0] || null)}
      />
      {file && (
        <button
          type="button"
          onClick={() => onChange(null)}
          className="text-[10px] text-red-500 hover:text-red-600 text-left w-fit"
        >
          Remove file
        </button>
      )}
      {error && <span className="text-[10px] text-red-500 flex items-center gap-0.5 mt-0.5"><AlertCircle size={9} />{error}</span>}
    </div>
  );
}

function SecLabel({ label }) {
  return (
    <div className="flex items-center gap-2 mb-2 mt-1">
      <span className="text-[11px] font-bold text-primary-700 uppercase tracking-wider">{label}</span>
      <div className="flex-1 h-px bg-primary-200" />
    </div>
  );
}

function G2({ children }) { return <div className="grid grid-cols-2 gap-x-4 gap-y-3">{children}</div>; }
function G3({ children }) { return <div className="grid grid-cols-3 gap-x-4 gap-y-3">{children}</div>; }
function G4({ children }) { return <div className="grid grid-cols-4 gap-x-4 gap-y-3">{children}</div>; }

// ─── Main Component ───────────────────────────────────────────────────────────
export default function AddEmployeeForm({ onClose, onSuccess }) {
  const dispatch = useDispatch();

  const [tab, setTab] = useState(0);
  const [visitedTabs, setVisited] = useState(new Set([0]));
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [toast, setToast] = useState(null);
  const [submitting, setSub] = useState(false);
  const [apiError, setApiError] = useState(null);

  const [files, setFiles] = useState(EMPTY_FILES);
  const setFile = (key, file) => setFiles(f => ({ ...f, [key]: file }));

  const [extraAddresses, setExtraAddresses] = useState([]);
  const addExtraAddress = () => setExtraAddresses(list => ([
    ...list,
    { id: `addr-${Date.now()}-${list.length}`, label: '', line1: '', line2: '', line3: '', city: '', postal: '' },
  ]));
  const removeExtraAddress = (id) => setExtraAddresses(list => list.filter(a => a.id !== id));
  const updateExtraAddress = (id, key, value) =>
    setExtraAddresses(list => list.map(a => a.id === id ? { ...a, [key]: value } : a));

  const [qualifications, setQualifications] = useState([
    { id: 'qual-0', name: '', fieldOfStudy: '', institute: '', result: '', grade: '', gpa: '' }
  ]);
  const addQualification = () => setQualifications(list => ([
    ...list,
    { id: `qual-${Date.now()}-${list.length}`, name: '', fieldOfStudy: '', institute: '', result: '', grade: '', gpa: '' }
  ]));
  const removeQualification = (id) => setQualifications(list => list.filter(q => q.id !== id));
  const updateQualification = (id, key, value) =>
    setQualifications(list => list.map(q => q.id === id ? { ...q, [key]: value } : q));

  const [bankAccounts, setBankAccounts] = useState([
    { id: 'bank-0', bankId: '', branchId: '', accountNumber: '', accountType: DEFAULT_ACCOUNT_TYPE, isPrimary: true }
  ]);
  const addBankAccount = () => setBankAccounts(list => ([
    ...list,
    { id: `bank-${Date.now()}-${list.length}`, bankId: '', branchId: '', accountNumber: '', accountType: DEFAULT_ACCOUNT_TYPE, isPrimary: list.length === 0 }
  ]));
  const removeBankAccount = (id) => setBankAccounts(list => {
    const removed = list.find(b => b.id === id);
    const rest = list.filter(b => b.id !== id);
    // Removing the primary account hands "primary" to the first one left.
    if (removed?.isPrimary && rest.length > 0 && !rest.some(b => b.isPrimary)) {
      return rest.map((b, i) => (i === 0 ? { ...b, isPrimary: true } : b));
    }
    return rest;
  });
  const updateBankAccount = (id, key, value) => {
    setBankAccounts(list => list.map(b => {
      if (b.id === id) return { ...b, [key]: value };
      // Only one primary account per employee: ticking one unticks the rest.
      if (key === 'isPrimary' && value === true) return { ...b, isPrimary: false };
      return b;
    }));
    if (key === 'isPrimary') setErrors(e => ({ ...e, bankPrimary: undefined }));
  };

  // ─── Dropdown data ────────────────────────────────────────────────────────
  const [ddLoading, setDdLoading] = useState(true);
  const [designations, setDesignations] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [locations, setLocations] = useState([]);
  const [grades, setGrades] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [banks, setBanks] = useState([]);
  const [branches, setBranches] = useState([]);
  const [employees, setEmployees] = useState([]);

  const showToast = useCallback((msg, type = 'error') => setToast({ message: msg, type }), []);

  const set = (key, val) => {
    setForm(f => ({ ...f, [key]: val }));
    setErrors(e => ({ ...e, [key]: undefined }));
    if (key === 'bankId') setForm(f => ({ ...f, bankId: val, branchId: '' }));
  };

  // ─── Load all dropdowns with fallbacks via axios ───────────────────────────
  useEffect(() => {
    const token = localStorage.getItem('AuthKey') || localStorage.getItem('auth-key') || '';
    if (token) axios.defaults.headers.common['auth-key'] = token;

    setDdLoading(true);
    Promise.all([
      safeFetch(() => designationService.GetDesignationList(), 'EMP/GetDevDetails'),
      safeFetch(() => departmentService.GetDepartmentList(), 'EMP/GetDepDetails'),
      safeFetch(() => locationService.GetLocationList(), 'EMP/GetLocDetails'),
      safeFetch(() => gradeService.GetGradeList(), 'EMP/GetProfDetails'),
      safeFetch(() => shiftService.GetShiftList(), 'EMP/GetShiftTypes'),
      safeFetch(() => employeeCategoryService.GetEmployeeCategoryList(), 'EMP/GetEMPCat'),
      safeFetch(() => bankService.GetBankList(), 'BankDetails/GetBanks'),
      safeFetch(() => bankBranchService.getBankBranchList(), 'BankDetails/GetAllBranch'),
      safeFetch(() => employeeService.GetEmployeeList(), 'EMP/GetEmployees'),
    ]).then(([desig, dept, loc, grade, shift, cat, bank, branch, emp]) => {
      setDesignations(toOpts(desig,
        ['DesignationId', 'designationId', 'HDS_DESIGNATION_ID', 'HDS_DESIGNATION_CODE', 'p_des_code', 'DesignationCode'],
        ['DesignationName', 'designationName', 'HDS_DESIGNATION_NAME', 'HDS_DESIGNATION_DESC', 'p_des_name', 'DesignationDesc']
      ));
      setDepartments(toOpts(dept,
        ['DepartmentId', 'departmentId', 'HDD_DEPARTMENT_ID', 'HDD_DEP_ID', 'HDD_DEP_CODE', 'p_department_code'],
        ['DepartmentName', 'departmentName', 'HDD_DEPARTMENT_NAME', 'HDD_DEP_NAME', 'HDD_DEP_DESC', 'p_department_name']
      ));
      setLocations(toOpts(loc,
        ['LocationId', 'locationId', 'HLD_LOCATION_ID', 'HLD_LOC_ID', 'HLD_LOC_CODE', 'p_location_code', 'p_loc_code'],
        ['LocationName', 'locationName', 'HLD_LOCATION_NAME', 'HLD_LOC_NAME', 'HLD_LOC_DESC', 'p_location_name', 'p_loc_name']
      ));
      setGrades(toOpts(grade,
        ['GradeId', 'gradeId', 'HGC_GRADE_ID', 'HGC_GRADE_CODE', 'p_grade_code', 'p_initial_grade'],
        ['GradeName', 'gradeName', 'HGC_GRADE_NAME', 'HGC_GRADE_DESC', 'p_grade_name']
      ));
      setShifts(toOpts(shift,
        ['ShiftId', 'shiftId', 'HSC_SHIFT_ID', 'HSC_SHIFT_CODE', 'p_shift_code'],
        ['ShiftName', 'shiftName', 'HSC_SHIFT_NAME', 'HSC_SHIFT_DESC', 'p_shift_name']
      ));
      setCategories(toOpts(cat,
        ['HEC_EMPLOYMENT_CATEGORY_ID', 'CategoryId', 'categoryId', 'HEC_CATEGORY_ID', 'HEC_CATEGORY_CODE', 'p_cat_code'],
        ['CategoryName', 'categoryName', 'HEC_CATEGORY_NAME', 'HEC_CATEGORY_DESC', 'p_cat_name']
      ));
      // Only approved master records (HBD_IS_APPROVED / HBB_IS_APPROVED = "1")
      // may be picked here — pending banks/branches stay out of the dropdowns.
      const approvedBanks = (bank || []).filter(isMasterApproved);
      const approvedBranches = (branch || []).filter(isMasterApproved);

      setBanks(toOpts(approvedBanks,
        ['HBD_BANK_ID', 'BankId', 'bankId', 'BankID', 'BankCode'],
        ['HBD_BANK_NAME', 'BankName', 'bankName', 'BankDesc']
      ));
      setBranches(approvedBranches.map(b => {
        const id = pick(b, ['HBB_BRANCH_ID', 'BranchId', 'branchId', 'BranchID', 'BranchCode']);
        const name = pick(b, ['HBB_BRANCH_NAME', 'BranchName', 'branchName', 'BranchDesc']);
        const code = pick(b, ['HBB_BRANCH_CODE', 'BranchCode', 'branchCode']);
        const bankId = pick(b, ['HBB_BANK_ID', 'BankId', 'bankId', 'BankID']);
        return id ? { value: String(id), label: `${name || id} ${code ? `(${code})` : ''}`, bankId: String(bankId) } : null;
      }).filter(Boolean));
      setEmployees(emp.map(e => {
        const id = pick(e, ['HedEmployeeId', 'EmployeeId', 'hedEmployeeId', 'employeeId', 'Id', 'id', 'HED_EMPLOYEE_ID']);
        const svcNo = pick(e, ['HedServiceNo', 'ServiceNo', 'hedServiceNo', 'serviceNo', 'HED_SERVICE_NO', 'p_ced_service_no']);
        const name = pick(e, ['HedReportName', 'ReportName', 'reportName', 'HED_REPORT_NAME', 'p_report_name']);
        const firstName = pick(e, ['HedFirstName', 'FirstName', 'firstName', 'HED_FIRST_NAME', 'p_first_name']);
        const lastName = pick(e, ['HedLastName', 'LastName', 'lastName', 'HED_LAST_NAME', 'p_last_name']);
        const fullName = name || `${firstName} ${lastName}`.trim();
        return id ? { value: String(id), label: `${fullName || id} ${svcNo ? `(${svcNo})` : ''}` } : null;
      }).filter(Boolean));
    }).finally(() => setDdLoading(false));
  }, []);

  const filteredBranches = form.bankId ? branches.filter(b => b.bankId === form.bankId) : branches;

  // ─── Validation ───────────────────────────────────────────────────────────
  const FIELD_TAB = {
    // Tab 0 – Personal
    serviceNo: 0, epfNo: 0, nicNo: 0, dateOfBirth: 0,
    firstName: 0, lastName: 0, initials: 0,
    mobileNo: 0, homeNo: 0, officeNo: 0,
    personalEmail: 0, companyEmail: 0,
    // Tab 1 – Address
    permanentAddressPostal: 1, currentAddressPostal: 1,
    // Tab 2 – Employment
    designationId: 2, dateOfJoining: 2, totalExperience: 2, otRate: 2,
    // Tab 3 – Emergency
    altContactNumber: 3,
    // Tab 4 – Other
    bankPrimary: 4,
  };

  // Dynamic keys
  const tabOf = (key) =>
    key.startsWith('gpa_') || key.startsWith('accountNumber_')
      ? 4
      : FIELD_TAB[key];

  // ─── Pure collector: runs every check, no side effects ─────────────────────
  const collectErrors = () => {
    const e = {};

    // ── Personal ────────────────────────────────────────
    const staffIdErr = checkStaffId(form.serviceNo);
    if (staffIdErr) e.serviceNo = staffIdErr;

    const epfErr = checkEpf(form.epfNo);
    if (epfErr) e.epfNo = epfErr;

    const dobErr = checkDateOfBirth(form.dateOfBirth);
    if (dobErr) e.dateOfBirth = dobErr;

    if (!fmt(form.firstName)) e.firstName = 'First name is required';
    if (!fmt(form.lastName)) e.lastName = 'Last name is required';
    if (!fmt(form.initials)) e.initials = 'Initials are required';

    const nicErr = checkNic(form.nicNo);
    if (nicErr) e.nicNo = nicErr;

    const personalEmailErr = checkEmail(form.personalEmail, 'Personal email');
    if (personalEmailErr) e.personalEmail = personalEmailErr;

    const companyEmailErr = checkEmail(form.companyEmail, 'Company email');
    if (companyEmailErr) e.companyEmail = companyEmailErr;

    const mobileErr = checkPhone(form.mobileNo, 'Mobile no');
    if (mobileErr) e.mobileNo = mobileErr;
    const homeErr = checkPhone(form.homeNo, 'Home phone');
    if (homeErr) e.homeNo = homeErr;
    const officeErr = checkPhone(form.officeNo, 'Office phone');
    if (officeErr) e.officeNo = officeErr;
    const altErr = checkPhone(form.altContactNumber, 'Emergency contact no');
    if (altErr) e.altContactNumber = altErr;

    // ── Address ─────────────────────────────────────────
    const permPostalErr = checkPostal(form.permanentAddressPostal);
    if (permPostalErr) e.permanentAddressPostal = permPostalErr;
    const currPostalErr = checkPostal(form.currentAddressPostal);
    if (currPostalErr) e.currentAddressPostal = currPostalErr;

    // ── Employment ──────────────────────────────────────
    if (!form.designationId) e.designationId = 'Designation is required';
    const dojErr = checkDateOfJoining(form.dateOfJoining);
    if (dojErr) e.dateOfJoining = dojErr;

    const expParts = parseExperience(form.totalExperience);
    const expErr = checkExperience(expParts.years, expParts.months);
    if (expErr) e.totalExperience = expErr;

    const otErr = checkOtRate(form.otRate);
    if (otErr) e.otRate = otErr;

    // ── Other: GPA per row ──────────────────────────────
    qualifications.forEach(q => {
      const gpaErr = checkGpa(q.gpa);
      if (gpaErr) e[`gpa_${q.id}`] = gpaErr;
    });

    // ── Other: account number per row ───────────────────
    bankAccounts.forEach(b => {
      const accErr = checkAccountNumber(b.accountNumber);
      if (accErr) e[`accountNumber_${b.id}`] = accErr;
    });

    // ── Other: exactly one primary account ──────────────
    const filledBanks = bankAccounts.filter(b => b.bankId && b.branchId && b.accountNumber);
    const primaryCount = filledBanks.filter(b => b.isPrimary).length;
    if (primaryCount > 1) {
      e.bankPrimary = 'Only one bank account can be the primary account';
    } else if (filledBanks.length > 0 && primaryCount === 0) {
      e.bankPrimary = 'Select one bank account as the primary account';
    }

    return e;
  };

  // ─── Full-form validation (used by Save) ───────────────────────────────────
  const validate = () => {
    const all = collectErrors();
    setErrors(all);
    if (Object.keys(all).length === 0) return true;

    const firstKey = Object.keys(all)[0];
    showToast(all[firstKey], 'error');
    const firstTab = tabOf(firstKey);
    if (firstTab !== undefined) goTab(firstTab);
    return false;
  };

  // ─── Per-tab validation (used by Next) ─────────────────────────────────────
  const validateTab = (tabId) => {
    const all = collectErrors();
    const tabErrs = {};
    Object.entries(all).forEach(([k, msg]) => {
      if (tabOf(k) === tabId) tabErrs[k] = msg;
    });

    // Clear stale errors for this tab, then merge the new ones
    setErrors(prev => {
      const next = { ...prev };
      Object.keys(next).forEach(k => {
        if (tabOf(k) === tabId) delete next[k];
      });
      return { ...next, ...tabErrs };
    });

    const keys = Object.keys(tabErrs);
    if (keys.length === 0) return true;

    showToast(tabErrs[keys[0]], 'error');
    return false;
  };

  // ─── Submit ───────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (!validate()) return;
    setSub(true);
    setApiError(null);
    try {
      const reportName = fmt(form.reportName) ||
        [form.firstName, form.middleName, form.lastName].filter(Boolean).join(' ');

      const payload = {
        ActionType: 1,
        p_hedServiceNo: fmt(form.serviceNo),
        p_hedEpfNo: fmt(form.epfNo),
        p_hedEtfNo: fmt(form.epfNo),
        p_hedTitle: fmt(form.title) || 'Mr.',
        p_hedFirstName: fmt(form.firstName),
        p_hedMiddleName: fmt(form.middleName),
        p_hedLastName: fmt(form.lastName),
        p_hedInitials: fmt(form.initials),
        p_hedReportName: reportName,
        p_hedGender: fmt(form.gender),
        p_hedDateOfBirth: fmt(form.dateOfBirth),
        // A typed lowercase v is sent as a capital V.
        p_hedNicNo: normaliseNic(form.nicNo),
        p_hedPassportNo: fmt(form.passportNo),
        p_hedMaritalStatus: fmt(form.maritalStatus),
        p_hedReligion: fmt(form.religion),
        p_hedNationality: '1',
        p_hedBloodGroup: form.bloodGroup ? String(form.bloodGroup) : '7',

        p_hedMobileNo: fmt(form.mobileNo),
        p_hedHomeNo: fmt(form.homeNo),
        p_hedOfficeNo: fmt(form.officeNo),
        p_hedPersonalEmail: fmt(form.personalEmail),
        p_hedCompanyEmail: fmt(form.companyEmail),

        p_hedDepartmentId: form.departmentId ? Number(form.departmentId) : null,
        p_hedLocationId: form.locationId ? Number(form.locationId) : null,
        p_hedDesignationId: form.designationId ? Number(form.designationId) : null,
        p_hedGradeId: form.gradeId ? Number(form.gradeId) : null,
        p_hedEmploymentCategoryId: form.employmentCategoryId ? Number(form.employmentCategoryId) : null,
        p_hedShiftId: form.shiftId ? Number(form.shiftId) : null,

        p_hedDateOfJoining: fmt(form.dateOfJoining),
        p_hedConfirmationDate: '',
        p_hedNoticePeriodDays: form.noticePeriodDays ? Number(form.noticePeriodDays) : null,
        p_OT: form.otRate ? Number(form.otRate) : null,
        p_hedReportingEmployeeId: form.reportingEmployeeId ? Number(form.reportingEmployeeId) : null,

        p_hedIsSystemUser: false,
        p_hedCreatedBy: getLoggedInServiceNo(),
        p_hedIsActive: true,
        p_hedRemarks: fmt(form.remarks),
        p_hedGroup: fmt(form.group),
        p_hedTotalExperiance: fmt(form.totalExperience),
        p_hedCostCenter: fmt(form.costCenter),

        // Permanent address
        p_heaAddressType: 'P',
        p_heaAddressLine1: fmt(form.permanentAddressLine1),
        p_heaAddressLine2: fmt(form.permanentAddressLine2),
        p_heaAddressLine3: fmt(form.permanentAddressLine3),
        p_heaCity: fmt(form.permanentAddressCity),
        p_heaDistrictId: 2,
        p_heaProvinceId: 2,
        p_heaCountryId: 1,
        p_heaPostalCode: fmt(form.permanentAddressPostal),
        p_heaIsPrimary: true,
        p_heaCreatedBy: getLoggedInServiceNo(),
        p_heaIsActive: true,
        p_heaRemarks: '',

        // Bank
        p_hebBankId: form.bankId ? Number(form.bankId) : null,
        p_hebBranchId: form.branchId ? Number(form.branchId) : null,
        // Kept in step with the AddBankDetails calls below: the holder name is
        // the employee's initials, and the type comes from the first bank row.
        p_hebAccountName: fmt(form.initials),
        p_hebAccountNumber: fmt(form.accountNumber),
        p_hebAccountType: bankAccounts[0]?.accountType || DEFAULT_ACCOUNT_TYPE,
        p_hebIsPrimary: true,
        p_hebCreatedBy: getLoggedInServiceNo(),
        p_hebIsActive: true,
        p_hebRemarks: '',

        // New employees always start unapproved — an approver clears them later.
        p_isApproved: NEW_EMPLOYEE_IS_APPROVED,

        p_hecContactName: fmt(form.altContactName) || 'N/A',
        p_hecContactNumber: fmt(form.altContactNumber) || '',
        p_hecRelationship: fmt(form.altContactRelationship) || 'Other',
        p_hecContactType: 'Emergency',
        p_hecAddress: fmt(form.altContactAddress) || '',
        p_hecCreatedBy: getLoggedInServiceNo(),
        p_hecIsActive: true,
        p_hecRemarks: '',

        p_heqQualificationTypeId: 1,
        p_heqQualificationName: '',
        p_heqFieldOfStudy: '',
        p_heqInstitute: '',
        p_heqCountry: 'Sri Lanka',
        p_heqStartDate: null,
        p_heqCompletedDate: null,
        p_heqResult: '',
        p_heqGrade: '',
        p_heqGPA: '',
        p_heqCertificateNo: '',
        p_heqAttachment: '',
        p_heqIsHighest: true,
        p_heqCreatedBy: getLoggedInServiceNo(),
        p_heqIsActive: true,
        p_heqRemarks: '',
      };

      // 1. Add Employee
      const addRes = await employeeService.AddEmployee(payload);

      if (addRes?.data) {
        const resData = addRes.data;
        if (resData.StatusCode && resData.StatusCode !== 200 && resData.StatusCode !== 201 && resData.StatusCode !== 204) {
          throw new Error(resData.Message || resData.message || resData.Error || 'Failed to save new employee on server.');
        }
        if (resData.Success === false || resData.isSuccess === false) {
          throw new Error(resData.Message || resData.message || resData.Error || 'Failed to save new employee on server.');
        }
      }

      let employeeId = null;
      if (addRes?.data) {
        const rs = addRes.data.ResultSet ?? addRes.data.resultSet ?? addRes.data;
        if (typeof rs === 'number') employeeId = rs;
        else if (rs) employeeId = rs.EmployeeId ?? rs.employeeId ?? rs.HedEmployeeId ?? rs.HED_EMPLOYEE_ID ?? null;
      }
      if (!employeeId) {
        try {
          const listRes = await employeeService.GetEmployeeList();
          const list = extractList(listRes);
          const match = list.find(r => {
            const svcNo = r.HedServiceNo ?? r.ServiceNo ?? r.hedServiceNo ?? r.serviceNo ?? r.HED_SERVICE_NO;
            return String(svcNo) === fmt(form.serviceNo);
          });
          if (match) employeeId = match.HedEmployeeId ?? match.EmployeeId ?? match.hedEmployeeId ?? match.employeeId ?? match.Id ?? match.id ?? match.HED_EMPLOYEE_ID;
        } catch { /* ignore */ }
      }


      if (employeeId && (files.profilePic || files.nicDocument || files.birthCertificate || files.qualificationCertificate || files.traineeCertificate || files.otherCertificate)) {
        const docFormData = new FormData();
        if (files.profilePic) docFormData.append('ProfilePic', files.profilePic);
        if (files.nicDocument) docFormData.append('NIC', files.nicDocument);
        if (files.birthCertificate) docFormData.append('BirthCertificate', files.birthCertificate);
        if (files.qualificationCertificate) docFormData.append('QualificationCertificate', files.qualificationCertificate);
        if (files.traineeCertificate) docFormData.append('TraineeCertificate', files.traineeCertificate);
        if (files.otherCertificate) docFormData.append('OtherCertificate', files.otherCertificate);

        await employeeService.UploadEmployeeDocs(employeeId, docFormData)
          .catch(err => console.error('[UploadEmployeeDocs] error:', err));
      }


      if (employeeId && (form.permanentAddressLine1 || form.permanentAddressCity)) {
        await employeeService.AddEmployeeAddress({
          p_hedEmployeeId: employeeId,
          p_heaCreatedBy: getLoggedInServiceNo(),
          p_isActive: 'A',
          p_remarks: '',
          p_heaAddressType: 'P',
          p_heaAddressLine1: fmt(form.permanentAddressLine1),
          p_heaAddressLine2: fmt(form.permanentAddressLine2),
          p_heaAddressLine3: fmt(form.permanentAddressLine3),
          p_heaCity: fmt(form.permanentAddressCity),
          p_heaPostalCode: fmt(form.permanentAddressPostal),
        }).catch(err => console.error('[AddEmployeeAddress - Permanent] error:', err));
      }

      if (employeeId && (form.currentAddressLine1 || form.currentAddressCity)) {
        await employeeService.AddEmployeeAddress({
          p_hedEmployeeId: employeeId,
          p_heaCreatedBy: getLoggedInServiceNo(),
          p_isActive: 'A',
          p_remarks: '',
          p_heaAddressType: 'C',
          p_heaAddressLine1: fmt(form.currentAddressLine1),
          p_heaAddressLine2: fmt(form.currentAddressLine2),
          p_heaAddressLine3: fmt(form.currentAddressLine3),
          p_heaCity: fmt(form.currentAddressCity),
          p_heaPostalCode: fmt(form.currentAddressPostal),
        }).catch(err => console.error('[AddEmployeeAddress - Current] error:', err));
      }

      if (employeeId && extraAddresses.length > 0) {
        await Promise.all(
          extraAddresses
            .filter(addr => addr.line1 || addr.line2 || addr.line3 || addr.city)
            .map((addr, idx) =>
              employeeService.AddEmployeeAddress({
                p_hedEmployeeId: employeeId,
                p_heaCreatedBy: getLoggedInServiceNo(),
                p_isActive: 'A',
                p_remarks: fmt(addr.label),
                p_heaAddressType: extraAddressTypeCode(idx),
                p_heaAddressLine1: fmt(addr.line1),
                p_heaAddressLine2: fmt(addr.line2),
                p_heaAddressLine3: fmt(addr.line3),
                p_heaCity: fmt(addr.city),
                p_heaPostalCode: fmt(addr.postal),
              }).catch(() => { })
            )
        );
      }

      // 4. Qualifications — sequential loop, one AddEmployeeQualification call per row, real error logging.
      const validQualifications = qualifications.filter(q => fmt(q.name));
      if (employeeId && validQualifications.length > 0) {
        for (let idx = 0; idx < validQualifications.length; idx++) {
          const q = validQualifications[idx];
          try {
            const qualRes = await employeeService.AddEmployeeQualification({
              p_hedEmployeeId: employeeId,
              p_heqQualificationTypeId: 1,
              p_heqQualificationName: fmt(q.name),
              p_heqFieldOfStudy: fmt(q.fieldOfStudy),
              p_heqInstitute: fmt(q.institute),
              p_heqCountry: 'Sri Lanka',
              p_heqStartDate: null,
              p_heqCompletedDate: null,
              p_heqResult: fmt(q.result),
              p_heqGrade: fmt(q.grade),
              p_heqGPA: fmt(q.gpa),
              p_heqCertificateNo: '',
              p_heqAttachment: '',
              p_heqIsHighest: idx === 0,
              p_heqCreatedBy: getLoggedInServiceNo(),
              p_heqIsActive: true,
              p_heqRemarks: '',
            });
            console.log(`[AddEmployeeQualification] row ${idx + 1} ("${q.name}") response:`, qualRes?.data);
          } catch (err) {
            console.error(`[AddEmployeeQualification] row ${idx + 1} ("${q.name}") FAILED:`, err?.response?.data || err);
          }
        }
      }
      // 6. Bank Details (AddBankDetails API)
      const validBanks = bankAccounts.filter(b => b.bankId && b.branchId && b.accountNumber);
      if (employeeId && validBanks.length > 0) {
        await Promise.all(
          validBanks.map((b, idx) =>
            employeeService.AddBankDetails({
              p_hebBankId: Number(b.bankId),
              p_hebBranchId: Number(b.branchId),
              // Account holder name is always the employee's initials.
              p_hebAccountName: fmt(form.initials),
              p_hebAccountNumber: fmt(b.accountNumber),
              p_hebAccountType: b.accountType || DEFAULT_ACCOUNT_TYPE,
              p_hebIsPrimary: b.isPrimary ? 1 : 0,
              p_hebCreatedBy: getLoggedInServiceNo(),
              p_hedEmployeeId: Number(employeeId),
              // HEB_IS_APPROVED is NOT NULL, and a new account is never
              // pre-approved — only the Approve tick sends 1.
              p_isApproved: NEW_BANK_IS_APPROVED,
            }).catch(err => console.error('[AddBankDetails] error:', err))
          )
        );
      }

      // 5a. Employment-detail workaround — AddEmployee doesn't reliably persist Department/Category
      if (employeeId && (form.departmentId || form.employmentCategoryId || form.gradeId || form.locationId || form.shiftId)) {
        const fixUpDiff = { p_hedServiceNo: fmt(form.serviceNo) };
        if (form.departmentId) fixUpDiff.p_hedDepartmentId = Number(form.departmentId);
        if (form.employmentCategoryId) fixUpDiff.p_hedEmploymentCategoryId = Number(form.employmentCategoryId);
        if (form.gradeId) fixUpDiff.p_hedGradeId = Number(form.gradeId);
        if (form.locationId) fixUpDiff.p_hedLocationId = Number(form.locationId);
        if (form.shiftId) fixUpDiff.p_hedShiftId = Number(form.shiftId);

        await employeeService.UpdateEmployee(fixUpDiff)
          .catch(err => console.error('[UpdateEmployee - employment-detail workaround] error:', err));
      }

      // 6a. Emergency Contact (AddEmployeeContact API)
      if (employeeId && fmt(form.altContactName)) {
        await employeeService.AddEmployeeContact({
          p_hedEmployeeId: employeeId,
          p_hecContactName: fmt(form.altContactName) || 'N/A',
          p_hecContactNumber: fmt(form.altContactNumber) || '',
          p_hecRelationship: fmt(form.altContactRelationship) || 'Other',
          p_hecContactType: 'Emergency',
          p_hecAddress: fmt(form.altContactAddress) || '',
          p_hecCreatedBy: getLoggedInServiceNo(),
          p_hecIsActive: true,
          p_hecRemarks: '',
        }).catch(err => console.error('[AddEmployeeContact] error:', err));
      }

      showToast('Employee added successfully!', 'success');
      setForm(EMPTY);
      setFiles(EMPTY_FILES);
      setExtraAddresses([]);
      setBankAccounts([{ id: 'bank-0', bankId: '', branchId: '', accountNumber: '', accountType: DEFAULT_ACCOUNT_TYPE, isPrimary: true }]);
      setQualifications([{ id: 'qual-0', name: '', fieldOfStudy: '', institute: '', result: '', grade: '', gpa: '' }]);
      dispatch(fetchAllEmployees());
      setTimeout(() => { if (onSuccess) onSuccess(); onClose(); }, 1500);
    } catch (err) {
      const msg = err?.response?.data?.Message || err?.response?.data?.message || err?.message || 'Failed to save employee.';
      showToast(msg, 'error');
      setApiError(msg);
    } finally {
      setSub(false);
    }
  };

  const goTab = (t) => { setTab(t); setVisited(v => new Set([...v, t])); };
  const goNext = () => {
    if (!validateTab(tab)) return;         
    goTab(Math.min(tab + 1, TABS.length - 1));
  };
  const goPrev = () => goTab(Math.max(tab - 1, 0));

  // ─── Tab Panels ───────────────────────────────────────────────────────────
  const renderTab = () => {
    switch (tab) {

      // ── PERSONAL ──────────────────────────────────────────────────────────
      case 0: return (
        <div className="space-y-4">
          <SecLabel label="Identification" />
          <G4>
            <Field label="Staff ID" required error={errors.serviceNo}>
              <Inp id="serviceNo" value={form.serviceNo} onChange={v => set('serviceNo', sanitiseStaffId(v))} placeholder="Staff ID (max 10)" error={errors.serviceNo} />
            </Field>
            <Field label="EPF No" >
              <Inp value={form.epfNo} onChange={v => set('epfNo', sanitiseEpf(v))} placeholder="EPF No (max 5)" error={errors.epfNo} />
            </Field>
            <Field label="NIC No" required error={errors.nicNo}>
              <Inp value={form.nicNo} onChange={v => set('nicNo', sanitiseNic(v))} placeholder="200012345678 or 200012345V" error={errors.nicNo} />
            </Field>
            <Field label="Passport No">
              <Inp value={form.passportNo} onChange={v => set('passportNo', v)} placeholder="P123456" />
            </Field>
          </G4>

          <SecLabel label="Personal Details" />
          <G4>
            <Field label="Title">
              <Sel value={form.title} onChange={v => set('title', v)} options={TITLES.map(t => ({ value: t, label: t }))} />
            </Field>
            <Field label="Gender">
              <Sel value={form.gender} onChange={v => set('gender', v)} options={GENDERS} />
            </Field>
            <Field label="First Name" required error={errors.firstName}>
              <Inp value={form.firstName} onChange={v => set('firstName', v)} placeholder="First name" error={errors.firstName} />
            </Field>
            <Field label="Middle Name">
              <Inp value={form.middleName} onChange={v => set('middleName', v)} placeholder="Middle name" />
            </Field>
          </G4>
          <G4>
            <Field label="Last Name" required error={errors.lastName}>
              <Inp value={form.lastName} onChange={v => set('lastName', v)} placeholder="Last name" error={errors.lastName} />
            </Field>
            <Field label="Initials" required error={errors.initials}>
              <Inp value={form.initials}
                onChange={v => set('initials', sanitiseInitials(v))}
                error={errors.initials} />
            </Field>
            <Field label="Report Name">
              <Inp value={form.reportName} onChange={v => set('reportName', v)} placeholder="Auto-filled if blank" />
            </Field>
          </G4>
          <G4>
            <Field label="Date of Birth" required error={errors.dateOfBirth}>
              <IsoDateInput
                value={form.dateOfBirth}
                onChange={v => set('dateOfBirth', v)}
                max={maxDobIso()}
                className={iCls(errors.dateOfBirth)}
              />
            </Field>
            <Field label="Marital Status">
              <Sel value={form.maritalStatus} onChange={v => set('maritalStatus', v)} options={MARITAL_STATUSES} />
            </Field>
            <Field label="Religion">
              <Sel value={form.religion} onChange={v => set('religion', v)} options={RELIGIONS} placeholder="Select" />
            </Field>
            <Field label="Blood Group">
              <Sel value={form.bloodGroup} onChange={v => set('bloodGroup', v)} options={BLOOD_GROUPS} placeholder="Select" />
            </Field>
          </G4>

          <SecLabel label="Contact Details" />
          <G3>
            <Field label="Mobile No">
              <Inp value={form.mobileNo} onChange={v => set('mobileNo', sanitisePhone(v))} placeholder="0771234567" error={errors.mobileNo} />
            </Field>
            <Field label="Home Phone">
              <Inp value={form.homeNo} onChange={v => set('homeNo', sanitisePhone(v))} placeholder="0112345678" error={errors.homeNo} />
            </Field>
            <Field label="Office Phone">
              <Inp value={form.officeNo} onChange={v => set('officeNo', sanitisePhone(v))} placeholder="0112345678" error={errors.officeNo} />
            </Field>
            <Field label="Company Email" error={errors.companyEmail}>
              <Inp type="email" value={form.companyEmail} onChange={v => set('companyEmail', v)} placeholder="name@company.com" error={errors.companyEmail} />
            </Field>
            <Field label="Personal Email" error={errors.personalEmail}>
              <Inp type="email" value={form.personalEmail} onChange={v => set('personalEmail', v)} placeholder="name@gmail.com" error={errors.personalEmail} />
            </Field>
          </G3>

          <SecLabel label="Profile Photo & Documents" />
          <div className="flex items-center gap-5 p-4 bg-gray-50 border border-gray-200 rounded-xl mb-4">
            {/* Circular Avatar Preview */}
            <div className="relative shrink-0">
              <div className="w-24 h-24 rounded-full overflow-hidden border-2 border-white shadow-md ring-4 ring-primary-100 bg-gray-200 flex items-center justify-center">
                {files.profilePic ? (
                  <img
                    src={URL.createObjectURL(files.profilePic)}
                    alt="Profile Preview"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <User size={38} className="text-gray-400" />
                )}
              </div>
              <label
                htmlFor="file-profile-pic-avatar"
                className="absolute bottom-0 right-0 bg-primary-600 hover:bg-primary-700 text-white p-2 rounded-full shadow-lg cursor-pointer transition-transform hover:scale-110"
                title="Upload Profile Picture"
              >
                <Upload size={13} />
              </label>
              <input
                id="file-profile-pic-avatar"
                type="file"
                accept="image/*"
                className="hidden"
                onChange={e => setFile('profilePic', e.target.files?.[0] || null)}
              />
            </div>

            <div className="flex-1 min-w-0">
              <p className="text-xs font-bold text-gray-800">Profile Picture</p>
              <p className="text-[11px] text-gray-500 mt-0.5 truncate">
                {files.profilePic ? files.profilePic.name : 'Upload employee profile photo (PNG, JPG or WEBP)'}
              </p>
              <div className="flex items-center gap-3 mt-2.5">
                <label
                  htmlFor="file-profile-pic-avatar"
                  className="h-9 inline-flex items-center px-3 bg-primary-50 border border-primary-200 text-primary-700 rounded-lg text-xs font-semibold hover:bg-primary-100 transition-colors cursor-pointer"
                >
                  {files.profilePic ? 'Change Photo' : 'Upload Photo'}
                </label>
                {files.profilePic && (
                  <button
                    type="button"
                    onClick={() => setFile('profilePic', null)}
                    className="text-xs text-red-500 hover:text-red-600 font-medium flex items-center gap-1"
                  >
                    <Trash2 size={12} /> Remove
                  </button>
                )}
              </div>
            </div>
          </div>

          <G3>
            <FileField
              label="NIC Copy"
              file={files.nicDocument}
              onChange={f => setFile('nicDocument', f)}
              accept="image/*,.pdf"
            />
            <FileField
              label="Birth Certificate"
              file={files.birthCertificate}
              onChange={f => setFile('birthCertificate', f)}
              accept="image/*,.pdf"
            />
            <FileField
              label="Qualification Certificate"
              file={files.qualificationCertificate}
              onChange={f => setFile('qualificationCertificate', f)}
              accept="image/*,.pdf"
            />
            <FileField
              label="Trainee Certificate"
              file={files.traineeCertificate}
              onChange={f => setFile('traineeCertificate', f)}
              accept="image/*,.pdf"
            />
            <FileField
              label="Other Certificates"
              file={files.otherCertificate}
              onChange={f => setFile('otherCertificate', f)}
              accept="image/*,.pdf"
            />
          </G3>
        </div>
      );

      // ── ADDRESS ────────────────────────────────────────────────────────────
      case 1: return (
        <div className="space-y-4">
          <SecLabel label="Permanent Address" />
          <G2>
            <Field label="Address Line 1">
              <Inp value={form.permanentAddressLine1} onChange={v => set('permanentAddressLine1', v)} placeholder="No / Street" />
            </Field>
            <Field label="Address Line 2">
              <Inp value={form.permanentAddressLine2} onChange={v => set('permanentAddressLine2', v)} placeholder="Area / Building" />
            </Field>
          </G2>
          <G3>
            <Field label="Address Line 3">
              <Inp value={form.permanentAddressLine3} onChange={v => set('permanentAddressLine3', v)} placeholder="Town" />
            </Field>
            <Field label="City">
              <Inp value={form.permanentAddressCity} onChange={v => set('permanentAddressCity', v)} placeholder="City" />
            </Field>
            <Field label="Postal Code">
              <Inp value={form.permanentAddressPostal} onChange={v => set('permanentAddressPostal', sanitisePostal(v))} placeholder="5 digits" error={errors.permanentAddressPostal} />
            </Field>
          </G3>

          <SecLabel label="Current Address" />
          <G2>
            <Field label="Address Line 1">
              <Inp value={form.currentAddressLine1} onChange={v => set('currentAddressLine1', v)} placeholder="No / Street" />
            </Field>
            <Field label="Address Line 2">
              <Inp value={form.currentAddressLine2} onChange={v => set('currentAddressLine2', v)} placeholder="Area / Building" />
            </Field>
          </G2>
          <G3>
            <Field label="Address Line 3">
              <Inp value={form.currentAddressLine3} onChange={v => set('currentAddressLine3', v)} placeholder="Town" />
            </Field>
            <Field label="City">
              <Inp value={form.currentAddressCity} onChange={v => set('currentAddressCity', v)} placeholder="City" />
            </Field>
            <Field label="Postal Code">
              <Inp value={form.currentAddressPostal} onChange={v => set('currentAddressPostal', sanitisePostal(v))} placeholder="5 digits" error={errors.currentAddressPostal} />
            </Field>
          </G3>

          <SecLabel label="Additional Addresses" />
          <div className="space-y-3">
            {extraAddresses.map((addr, idx) => (
              <div key={addr.id} className="relative border border-gray-200 rounded p-3 bg-gray-50/60 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">
                    Address Type Code: {extraAddressTypeCode(idx)}
                  </span>
                  <button
                    type="button"
                    onClick={() => removeExtraAddress(addr.id)}
                    className="text-gray-400 hover:text-red-500 transition-colors"
                    title="Remove address"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
                <G2>
                  <Field label="Address Label">
                    <Inp value={addr.label} onChange={v => updateExtraAddress(addr.id, 'label', v)} placeholder="Work Address" />
                  </Field>
                  <Field label="City">
                    <Inp value={addr.city} onChange={v => updateExtraAddress(addr.id, 'city', v)} placeholder="City" />
                  </Field>
                </G2>
                <G2>
                  <Field label="Address Line 1">
                    <Inp value={addr.line1} onChange={v => updateExtraAddress(addr.id, 'line1', v)} placeholder="No / Street" />
                  </Field>
                  <Field label="Address Line 2">
                    <Inp value={addr.line2} onChange={v => updateExtraAddress(addr.id, 'line2', v)} placeholder="Area / Building" />
                  </Field>
                </G2>
                <G2>
                  <Field label="Address Line 3">
                    <Inp value={addr.line3} onChange={v => updateExtraAddress(addr.id, 'line3', v)} placeholder="Town" />
                  </Field>
                  <Field label="Postal Code">
                    <Inp value={addr.postal} onChange={v => updateExtraAddress(addr.id, 'postal', sanitisePostal(v))} placeholder="5 digits" />
                  </Field>
                </G2>
              </div>
            ))}

            <button
              type="button"
              onClick={addExtraAddress}
              className="flex items-center gap-1.5 h-9 px-3 border border-dashed border-primary-300 text-primary-600 rounded-lg text-xs font-semibold hover:bg-primary-50 transition-colors w-fit"
            >
              <Plus size={13} /> Add Address
            </button>
          </div>
        </div>
      );

      // ── EMPLOYMENT ─────────────────────────────────────────────────────────
      case 2: return (
        <div className="space-y-4">
          <SecLabel label="Employment Details" />

          <G2>
            <Field label="Designation" required error={errors.designationId}>
              <Sel
                id="designationId"
                value={form.designationId}
                onChange={v => set('designationId', v)}
                options={designations}
                loading={ddLoading}
                placeholder="Select Designation"
                error={errors.designationId}
              />
            </Field>

            <Field label="Department">
              <Sel
                value={form.departmentId}
                onChange={v => set('departmentId', v)}
                options={departments}
                loading={ddLoading}
                placeholder="Select Department"
              />
            </Field>
          </G2>

          <G2>
            <Field label="Location">
              <Sel
                value={form.locationId}
                onChange={v => set('locationId', v)}
                options={locations}
                loading={ddLoading}
                placeholder="Select Location"
              />
            </Field>

            <Field label="Grade">
              <Sel
                value={form.gradeId}
                onChange={v => set('gradeId', v)}
                options={grades}
                loading={ddLoading}
                placeholder="Select Grade"
              />
            </Field>
          </G2>

          <G2>
            <Field label="Employment Category">
              <Sel
                value={form.employmentCategoryId}
                onChange={v => set('employmentCategoryId', v)}
                options={categories}
                loading={ddLoading}
                placeholder="Select Category"
              />
            </Field>

            <Field label="Shift">
              <Sel
                value={form.shiftId}
                onChange={v => set('shiftId', v)}
                options={shifts}
                loading={ddLoading}
                placeholder="Select Shift"
              />
            </Field>
          </G2>

          <G3>
            <Field label="Date of Joining" required error={errors.dateOfJoining}>
              <IsoDateInput
                value={form.dateOfJoining}
                onChange={v => set('dateOfJoining', v)}
                max={todayIso()}
                className={iCls(errors.dateOfJoining)}
              />
            </Field>

            <Field label="Notice Period (days)">
              <Inp
                type="number"
                value={form.noticePeriodDays}
                onChange={v => set('noticePeriodDays', v)}
                placeholder="0"
              />
            </Field>

            <Field label="OT Rate (LKR)">
              <Inp value={form.otRate}
                onChange={v => set('otRate', sanitiseOtRate(v))}
                error={errors.otRate} />
            </Field>
          </G3>

          <G2>
            <Field label="Total Experience" error={errors.totalExperience}>
              <ExperienceInput
                value={form.totalExperience}
                onChange={v => set('totalExperience', v)}
                inputClassName={iCls(errors.totalExperience)}
              />
            </Field>

            <Field label="Reporting Employee">
              <Sel
                value={form.reportingEmployeeId}
                onChange={v => set('reportingEmployeeId', v)}
                options={employees}
                loading={ddLoading}
                placeholder="Select Reporting Employee"
              />
            </Field>
          </G2>

          <G2>
            <Field label="Cost Center">
              <Inp
                value={form.costCenter}
                onChange={v => set('costCenter', v)}
                placeholder="Cost Center"
              />
            </Field>

            <Field label="Group">
              <Inp
                value={form.group}
                onChange={v => set('group', v)}
                placeholder="Group"
              />
            </Field>
          </G2>
        </div>
      );

      // ── EMERGENCY ──────────────────────────────────────────────────────────
      case 3: return (
        <div className="space-y-4">
          <SecLabel label="Emergency Contact Details" />
          <G3>
            <Field label="Contact Name">
              <Inp value={form.altContactName} onChange={v => set('altContactName', v)} placeholder="Full name" />
            </Field>
            <Field label="Relationship">
              <Sel
                value={form.altContactRelationship}
                onChange={v => set('altContactRelationship', v)}
                options={RELATIONSHIP_OPTS}
                placeholder="Select"
              />
            </Field>
            <Field label="Contact Number">
              <Inp value={form.altContactNumber} onChange={v => set('altContactNumber', sanitisePhone(v))} placeholder="0771234567" error={errors.altContactNumber} />
            </Field>
          </G3>
          <G2>
            <Field label="Address">
              <Inp value={form.altContactAddress} onChange={v => set('altContactAddress', v)} placeholder="No / Street, City" />
            </Field>
          </G2>
        </div>
      );

      // ── OTHER ──────────────────────────────────────────────────────────────
      case 4: return (
        <div className="space-y-4">
          <div className="flex items-center justify-between mb-2 mt-1">
            <SecLabel label="Educational Qualifications" />
            <button
              type="button"
              onClick={addQualification}
              className="flex items-center gap-1 text-[12px] font-semibold text-primary-600 hover:text-primary-800 shrink-0"
            >
              <Plus size={13} /> Add Qualification
            </button>
          </div>
          <div className="space-y-3">
            {qualifications.map((q, idx) => (
              <div key={q.id} className="relative border border-gray-200 rounded-xl p-4 bg-gray-50/60">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-[12px] font-bold text-gray-700">Qualification {idx + 1}</span>
                  {qualifications.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeQualification(q.id)}
                      className="text-gray-400 hover:text-red-500 transition-colors"
                      title="Remove qualification"
                    >
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
                <G2>
                  <Field label="Qualification Name">
                    <Inp value={q.name} onChange={v => updateQualification(q.id, 'name', v)} placeholder="A/L, CIMA, HND" />
                  </Field>
                  <Field label="Result">
                    <Inp value={q.result} onChange={v => updateQualification(q.id, 'result', v)} placeholder=" 3 A's, Passed" />
                  </Field>
                  <Field label="Field of Study">
                    <Inp value={q.fieldOfStudy} onChange={v => updateQualification(q.id, 'fieldOfStudy', v)} placeholder="Science" />
                  </Field>
                  <Field label="Grade">
                    <Inp value={q.grade} onChange={v => updateQualification(q.id, 'grade', v)} placeholder=" A" />
                  </Field>
                  <Field label="Institute">
                    <Inp value={q.institute} onChange={v => updateQualification(q.id, 'institute', v)} placeholder="Institute name" />
                  </Field>
                  <Field label="GPA">
                    <Inp value={q.gpa}
                      onChange={v => updateQualification(q.id, 'gpa', sanitiseGpa(v))}
                      error={errors[`gpa_${q.id}`]} />
                  </Field>
                </G2>
              </div>
            ))}
          </div>
          <div className="max-w-md">
            <FileField
              label="Upload Qualification Certificate / Attachment"
              file={files.qualificationCertificate}
              onChange={f => setFile('qualificationCertificate', f)}
              accept="image/*,.pdf"
            />
          </div>
          <SecLabel label="Bank Details" />
          {errors.bankPrimary && (
            <p className="text-[11px] text-red-500 font-medium -mt-1">{errors.bankPrimary}</p>
          )}
          <div className="space-y-3">
            {bankAccounts.map((b, idx) => {
              const currentBranches = b.bankId ? branches.filter(br => br.bankId === b.bankId) : branches;
              return (
                <div key={b.id} className="relative border border-gray-200 rounded p-3 bg-gray-50/60 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-[11px] font-bold text-gray-700">
                      Bank Account #{idx + 1} {b.isPrimary ? '(Primary)' : ''}
                    </span>
                    {bankAccounts.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeBankAccount(b.id)}
                        className="text-gray-400 hover:text-red-500 transition-colors"
                        title="Remove bank account"
                      >
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                  <G2>
                    <Field label="Bank">
                      <Sel
                        value={b.bankId}
                        onChange={v => {
                          updateBankAccount(b.id, 'bankId', v);
                          updateBankAccount(b.id, 'branchId', '');
                        }}
                        options={banks}
                        loading={ddLoading}
                        placeholder="Select Bank"
                      />
                    </Field>
                    <Field label="Branch">
                      <Sel
                        value={b.branchId}
                        onChange={v => updateBankAccount(b.id, 'branchId', v)}
                        options={currentBranches}
                        loading={ddLoading}
                        placeholder="Select Branch"
                      />
                    </Field>
                  </G2>
                  <G2>
                    <Field label="Account Number">
                      <Inp value={b.accountNumber}
                        onChange={v => updateBankAccount(b.id, 'accountNumber', sanitiseAccountNumber(v))}
                        error={errors[`accountNumber_${b.id}`]} />
                    </Field>
                    <Field label="Account Type">
                      <Sel
                        value={b.accountType || DEFAULT_ACCOUNT_TYPE}
                        onChange={v => updateBankAccount(b.id, 'accountType', v || DEFAULT_ACCOUNT_TYPE)}
                        options={ACCOUNT_TYPES}
                        clearable={false}
                      />
                    </Field>
                  </G2>

                  {/* The account holder name is the employee's initials — it is
                      never typed here, so it can't disagree with the record. */}
                  <div className="flex items-center gap-2 text-[11px] text-gray-500">
                    <span className="font-semibold text-gray-600">Account Holder Name:</span>
                    {fmt(form.initials)
                      ? <span className="font-semibold text-gray-800">{fmt(form.initials)}</span>
                      : <span className="italic text-gray-400">set the employee's Initials on the Personal tab</span>}
                  </div>
                  <div className="flex items-center gap-2 pt-0.5">
                    <label className="flex items-center gap-2 text-xs text-gray-600 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={!!b.isPrimary}
                        onChange={e => updateBankAccount(b.id, 'isPrimary', e.target.checked)}
                        className="rounded border-gray-300 text-primary-600 focus:ring-primary-400"
                      />
                      Primary Account
                    </label>
                  </div>
                </div>
              );
            })}

            <button
              type="button"
              onClick={addBankAccount}
              className="flex items-center gap-1.5 h-9 px-3 border border-dashed border-primary-300 text-primary-600 rounded-lg text-xs font-semibold hover:bg-primary-50 transition-colors w-fit"
            >
              <Plus size={13} /> Add Bank Account
            </button>
          </div>

          <SecLabel label="Remarks" />
          <Field label="Remarks">
            <textarea value={form.remarks} onChange={e => set('remarks', e.target.value)} rows={3}
              className="w-full border border-gray-200 rounded-lg px-2.5 py-2 text-xs text-gray-800 outline-none focus:border-primary-500 resize-none bg-white transition-colors"
              placeholder="Additional remarks..." />
          </Field>
        </div>
      );

      default: return null;
    }
  };

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/60 overflow-y-auto py-4 px-2">
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      <div className="bg-white w-full max-w-5xl mx-auto my-auto shadow-2xl rounded" style={{ minHeight: 540 }}>

        {/* Title bar */}
        <div className="flex items-center justify-between px-4 py-2.5 bg-gray-700 rounded-t">
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 bg-primary-400 rounded-sm flex items-center justify-center">
              <User size={11} className="text-white" />
            </div>
            <span className="text-white text-[13px] font-semibold tracking-wide">Add New Employee</span>
          </div>
          <button onClick={onClose} className="text-gray-300 hover:text-white hover:bg-gray-600 transition-colors p-1 rounded">
            <X size={16} />
          </button>
        </div>

        {/* Loading bar */}
        {ddLoading && (
          <div className="flex items-center gap-2 px-4 py-1.5 bg-primary-50 border-b border-primary-200 text-primary-700 text-[11px]">
            <Loader2 size={11} className="animate-spin" /> Loading dropdown data from server...
          </div>
        )}

        {/* Tab bar */}
        <div className="flex border-b border-gray-200 bg-gray-50">
          {TABS.map(t => {
            const Icon = t.icon;
            const active = tab === t.id;
            const visited = visitedTabs.has(t.id) && !active;
            return (
              <button key={t.id} onClick={() => goTab(t.id)}
                className={`flex items-center gap-1.5 px-5 py-2.5 text-[12px] font-semibold border-b-2 transition-all whitespace-nowrap
                  ${active ? 'border-primary-600 text-primary-700 bg-white' : visited ? 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-100' : 'border-transparent text-gray-400 hover:text-gray-600 hover:bg-gray-100'}`}
              >
                <Icon size={13} className={active ? 'text-primary-600' : ''} />
                {t.label}
              </button>
            );
          })}
        </div>

        {/* Tab content */}
        <div className="p-5 overflow-y-auto bg-white" style={{ maxHeight: '62vh' }}>
          {apiError && (
            <div className="flex items-center gap-2 px-4 py-3 mb-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-xs font-medium">
              <AlertCircle size={14} className="shrink-0 text-red-600" />
              <span className="flex-1">{apiError}</span>
              <button onClick={() => setApiError(null)} className="text-red-400 hover:text-red-600">
                <X size={14} />
              </button>
            </div>
          )}
          {renderTab()}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-5 py-3 border-t border-gray-200 bg-gray-50 shrink-0">
          <button onClick={onClose}
            className="flex items-center gap-1.5 h-9 px-4 rounded-lg text-xs font-semibold bg-white border border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
            Cancel
          </button>

          <div className="flex items-center gap-2">
            <button onClick={goPrev} disabled={tab === 0}
              className="flex items-center gap-1.5 h-9 px-4 rounded-lg text-xs font-semibold bg-white border border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
              <ChevronLeft size={13} /> Prev
            </button>

            {tab < TABS.length - 1 ? (
              <button onClick={goNext}
                className="flex items-center gap-2 h-9 px-5 bg-primary-600 hover:bg-primary-700 text-white rounded-lg text-xs font-semibold transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed">
                Next <ChevronRight size={13} />
              </button>
            ) : (
              <button onClick={handleSubmit} disabled={submitting}
                className="flex items-center gap-2 h-9 px-5 bg-primary-600 hover:bg-primary-700 text-white rounded-lg text-xs font-semibold transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed">
                {submitting ? <><Loader2 size={13} className="animate-spin" />Saving...</> : <><CheckCircle size={13} />Save Employee</>}
              </button>
            )}
          </div>
        </div>
      </div>

      <style>{`
        @keyframes slideInRight { from { transform: translateX(120px); opacity: 0; } to { transform: translateX(0); opacity: 1; } }
      `}</style>
    </div>
  );
}